import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import { isLocalhost } from '@utils/environment'

export const STORAGE_KEY = 'chunk_reload_path'
export const RELOAD_GUARD_KEY = 'chunk_reload_attempts'
export const RELOAD_GUARD_WINDOW_MS = 30000
export const MAX_RELOADS_IN_WINDOW = 2
export const CACHE_BUST_PARAM = '_v'

/**
 * Detecta se um erro é de falha de carregamento de chunk (JS ou CSS).
 * JS chunks geram ChunkLoadError (webpack). CSS chunks geram erro com
 * code CSS_CHUNK_LOAD_FAIL (mini-css-extract-plugin).
 */
export function isChunkLoadError(error) {
  if (!error) return false
  if (error.name === 'ChunkLoadError') return true
  if (error.code === 'CSS_CHUNK_LOAD_FAIL') return true
  if (
    typeof error.message === 'string' &&
    /Loading (CSS )?chunk .+ failed/.test(error.message)
  ) {
    return true
  }
  return false
}

/**
 * Notifica o usuário que há uma nova versão disponível, disparando
 * o modal global "Nova versão disponível" via GlobalBus. O reload
 * acontece somente quando o usuário clica em "Atualizar agora".
 *
 * Usa sessionStorage para evitar disparar o modal múltiplas vezes
 * para o mesmo path na mesma sessão. Retorna true se o modal foi
 * emitido, false se já havia sido emitido para esse path.
 *
 * Fixes AAWZ-WEB-3VT
 */
export function handleChunkError() {
  try {
    const currentPath = globalThis.location.pathname + globalThis.location.search
    const lastReloadPath = sessionStorage.getItem(STORAGE_KEY)

    if (lastReloadPath === currentPath) {
      return false
    }

    sessionStorage.setItem(STORAGE_KEY, currentPath)
  } catch {
    // sessionStorage indisponível — emite modal sem proteção anti-duplicação
  }

  if (isLocalhost()) {
    // Em localhost o versao.json e os chunks no Flask local ficam
    // desincronizados entre builds de teste — o modal "Nova versão" so
    // atrapalha o desenvolvedor. O ChunkLoadError em si ainda eh logado
    // (acima, pelo path onde o handler eh chamado).
    return false
  }

  GlobalBus.$emit(MODAL.NEW_VERSION_AVAILABLE, { autoReload: true })
  return true
}

/**
 * Limpa a flag de notificação. Chamado no boot da SPA (após o reload
 * efetivo ter trazido o HTML novo) para permitir notificações futuras
 * em próximos deploys.
 */
export function clearChunkReloadFlag() {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // sessionStorage indisponível — nada a limpar
  }
}

function safeReadAttempts() {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((ts) => typeof ts === 'number' && Number.isFinite(ts))
  } catch {
    return []
  }
}

function safeWriteAttempts(attempts) {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify(attempts))
  } catch {
    // sessionStorage indisponível — segue sem persistência
  }
}

/**
 * Retorna true se já houve MAX_RELOADS_IN_WINDOW tentativas de reload nos
 * últimos RELOAD_GUARD_WINDOW_MS. Usado para evitar loop infinito quando
 * o HTML novo continua referenciando chunks que não existem (ex: CDN com
 * cache que não respeitou no-store).
 */
export function isInReloadLoop(now = Date.now()) {
  const recent = safeReadAttempts().filter(
    (ts) => now - ts < RELOAD_GUARD_WINDOW_MS,
  )
  return recent.length >= MAX_RELOADS_IN_WINDOW
}

/**
 * Registra mais uma tentativa de reload no sessionStorage para
 * alimentar isInReloadLoop().
 */
export function recordReloadAttempt(now = Date.now()) {
  const attempts = safeReadAttempts()
  attempts.push(now)
  const recent = attempts.filter((ts) => now - ts < RELOAD_GUARD_WINDOW_MS)
  safeWriteAttempts(recent)
}

/**
 * Limpa o histórico de tentativas de reload. Chamado quando o boot foi
 * bem-sucedido e a SPA está navegando normalmente — o usuário não está
 * em loop.
 */
export function clearReloadAttempts() {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY)
  } catch {
    // sessionStorage indisponível — nada a limpar
  }
}

/**
 * Constrói uma URL nova a partir da atual injetando um query param
 * único para forçar cache-miss no navegador/CDN. Mantém pathname,
 * hash e outros params.
 */
export function buildCacheBustUrl(href, timestamp = Date.now()) {
  try {
    const url = new URL(href)
    url.searchParams.set(CACHE_BUST_PARAM, String(timestamp))
    return url.toString()
  } catch {
    // ambiente sem URL global — devolve sem mexer
    return href
  }
}

/**
 * Recarrega a página invalidando cache do navegador via query param
 * único. Antes de recarregar, verifica se já não estamos em loop —
 * se estiver, retorna { reloaded: false, reason: 'loop' } para que
 * o caller possa mostrar uma mensagem ao usuário.
 *
 * Aceita um objeto `deps` apenas para facilitar testes (injeção de
 * Date.now e do location). Em produção é chamada sem argumentos.
 */
export function forceReload({
  now = Date.now(),
  location: loc = globalThis.location,
} = {}) {
  if (isInReloadLoop(now)) {
    return { reloaded: false, reason: 'loop' }
  }
  recordReloadAttempt(now)
  const nextHref = buildCacheBustUrl(loc.href, now)
  loc.replace(nextHref)
  return { reloaded: true, reason: null }
}
